<template>
  <div>
    <div class="v-form-builder-question__label">{{ i18n.nameOfRepeatableItem }}</div>
    <VInput v-model="question.repeatable_item_name" :error="blueprint.errors[question.id]?.repeatable_item_name" size="medium" style="margin-bottom: 10px" />

    <VFormBuilderQuestion v-for="childQuestion in childTemplateQuestions" :key="childQuestion.id" :blueprint :question-id="childQuestion.id">
      <template #question-header-right="slotProps">
        <slot name="question-header-right" v-bind="slotProps" />
      </template>
      <template #question-header-bottom="slotProps">
        <slot name="question-header-bottom" v-bind="slotProps" />
      </template>
      <template #answer="slotProps">
        <slot name="answer" v-bind="slotProps" />
      </template>
      <template #answer-label="slotProps">
        <slot name="answer-label" v-bind="slotProps" />
      </template>
      <template #dropdown-option="slotProps">
        <slot name="dropdown-option" v-bind="slotProps" />
      </template>
      <template #true-false-option="slotProps">
        <slot name="true-false-option" v-bind="slotProps" />
      </template>
    </VFormBuilderQuestion>

    <VFormBuilderSelectQuestion :blueprint :button-label="i18n.addQuestion" @select="addQuestion" />
  </div>
</template>

<script setup lang="ts">
import { computed, provide, watch } from 'vue'

import { i18nFormBuilder as i18n } from '../../utils/i18n.ts'
import { $blueprintQuestionTypesKey } from '../../utils/injections.ts'
import VInput from '../VInput.vue'
import { type Blueprint, type DropdownQuestion, type Question, type QuestionOption, type QuestionType, type RepeaterQuestion, type TrueFalseQuestion } from './useBlueprint.ts'
import VFormBuilderQuestion from './VFormBuilderQuestion.vue'
import VFormBuilderSelectQuestion from './VFormBuilderSelectQuestion.vue'

interface Props {
  blueprint: Blueprint
  question: RepeaterQuestion
}
const { blueprint, question } = defineProps<Props>()

defineSlots<{
  answer(props: { question: Question }): any
  'answer-label'(props: { question: Question }): any
  'question-header-right'(props: { question: Question }): any
  'question-header-bottom'(props: { question: Question }): any
  'true-false-option'(props: { question: TrueFalseQuestion; option: QuestionOption }): any
  'dropdown-option'(props: { question: DropdownQuestion; option: QuestionOption }): any
}>()

provide($blueprintQuestionTypesKey, blueprint.settings.repeater_question_types)

const childTemplateQuestions = computed(() => blueprint.questions.filter((q) => q.parent_question_id === question.id && q.is_template))

const nestedChildTemplateQuestions = computed(() => JSON.stringify(blueprint.gatherNestedChildQuestions(question).filter((q) => q.is_template)))

watch([question, nestedChildTemplateQuestions], () => syncFirstRepeatableItem(), { deep: true })

function syncFirstRepeatableItem(): void {
  for (const repeatedQuestion of blueprint.questions.filter((q) => !q.is_template && q.parent_question_id === question.id)) {
    const questionsToRemove = blueprint.gatherNestedChildQuestions(repeatedQuestion)
    questionsToRemove.push(repeatedQuestion)
    const questionIdsToRemove = questionsToRemove.map((question) => question.id)
    blueprint.questions = blueprint.questions.filter((question) => !questionIdsToRemove.includes(question.id))
  }

  const newQuestions = blueprint.createQuestionCopies({
    question,
    properties: { parent_question_id: question.id, is_template: false },
    omitProperties: ['is_template'],
    filter: (question) => question.is_template !== false,
  })
  blueprint.questions.push(...newQuestions)
}

async function addQuestion(type: QuestionType) {
  await blueprint.addQuestion(type, {
    parent_question_id: question.id,
    is_template: true,
    internal: question.internal,
  })
}
</script>
